import { cn } from "../../lib/utils"

type CountryFlagProps = {
  /** ISO 3166-1 alpha-2 code, e.g. "it" (from the marketplace's config country). */
  country: string
  className?: string
}

/** Renders a country as its flag emoji, e.g. "it" → 🇮🇹, so an operator can scan
 *  an order's origin at a glance. Flag glyphs render natively on macOS/iOS; on
 *  platforms without them (e.g. Windows) the browser falls back to the two
 *  regional-indicator letters, which is still a meaningful label. The uppercased
 *  code is exposed as the title/aria-label for accessibility and that fallback. */
export function CountryFlag({ country, className }: CountryFlagProps) {
  const code = country.toUpperCase()
  return (
    <span className={cn("shrink-0 text-sm leading-none", className)} title={code} aria-label={code}>
      {toFlagEmoji(code)}
    </span>
  )
}

// Map a two-letter code to its regional-indicator pair (U+1F1E6 is 'A').
const REGIONAL_INDICATOR_A = 0x1f1e6
const LETTER_A = "A".charCodeAt(0)

function toFlagEmoji(code: string): string {
  if (!/^[A-Z]{2}$/.test(code)) {
    return code
  }
  return String.fromCodePoint(
    ...[...code].map((c) => REGIONAL_INDICATOR_A + (c.charCodeAt(0) - LETTER_A))
  )
}
